import { PropsWithChildren, useState } from 'react';
import { Flex, Box } from 'reflexbox';
import styled from 'styled-components';
import { FormikProvider, Form, useFormik } from 'formik';
import * as Yup from 'yup';
import * as Sentry from '@sentry/react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Callout, Input, Label, Paragraph, Separator, Switch } from '@treams/treams-ui';

import * as graphql from '../../../../src/generated/graphql';

import GoogleTagManager from './GoogleTagManager';

import { Jumbo } from './Jumbo';
import { Sidebar } from './Sidebar';

import { useMixpanel, useMixpanelTrack } from 'packages/kakapo-ui-application/hooks/useMixpanel';
import LoginAttemptCard from './LoginAttemptCard';
import useLanguage from 'packages/kakapo-ui-application/hooks/useLanguage';

const Layout = styled(Flex)`
	font-family: 'Source Sans Pro', sans-serif;
`;

function useHubspotOnboardingForm() {
	const config = {
		portalId: '6955348',
		formId: 'd01cda29-1e24-4ed2-b932-ad51ba7d6da6',
	};

	return [
		async function createSubmission(input: graphql.RequestWorkspaceInput) {
			const url = `https://api.hsforms.com/submissions/v3/integration/submit/${config.portalId}/${config.formId}`;

			const data = {
				fields: [
					{
						name: 'company',
						value: input.companyName,
					},
					{
						name: 'user_name',
						value: input.userName,
					},
					{
						name: 'email',
						value: input.email,
					},
				],
				context: {
					pageUri: 'https://try.ontreams.com',
					pageName: 'Free trial request page',
				},
			};

			await fetch(url, {
				method: 'POST',
				body: JSON.stringify(data),
				headers: {
					'Content-Type': 'application/json',
				},
			}).catch(() =>
				Sentry.captureException(new Error('Failed sending free trial request to HubSpot.')),
			);
		},
	];
}

const StyledLink = styled.a`
	text-decoration: underline;

	&:hover {
		text-decoration: underline !important;
		opacity: 0.7;
	}
`;

function TermsLink(props: PropsWithChildren & { href: string }) {
	return (
		<StyledLink href={props.href} target="_blank" rel="noreferrer">
			{props.children}
		</StyledLink>
	);
}

export function Onboarding() {
	const { t } = useTranslation();
	const { id: locale } = useLanguage();

	const [create, { loading }] = graphql.useRequestWorkspaceMutation();
	const [createSubmission] = useHubspotOnboardingForm();

	const [error, setError] = useState<Error | undefined>(undefined);
	const [focus, setFocus] = useState<string | undefined>();

	useMixpanelTrack('/free-trial');
	useMixpanelTrack(focus && `signup.${focus}`);

	const mixpanel = useMixpanel();

	const formik = useFormik({
		onSubmit: async (values) => {
			const input: graphql.RequestWorkspaceInput = {
				userName: values.userName,
				companyName: values.companyName,
				email: values.email,
				locale,
			};

			try {
				await create({
					variables: {
						input,
					},
				});
				await createSubmission(input);
			} catch (err) {
				setError(err);
				return;
			}

			window.location.href = 'https://www.treams.nl/bedankt-proefperiode/';
		},
		initialValues: {
			userName: '',
			companyName: '',
			email: '',
			acceptsPolicies: false,
		},
		validationSchema: Yup.object({
			userName: Yup.string().required().min(3),
			companyName: Yup.string().required().min(3),
			email: Yup.string().email().required(),
			acceptsPolicies: Yup.boolean().equals([true]).required(),
		}).required(),
	});

	return (
		<Layout style={{ minHeight: '100vh' }} flexWrap="wrap">
			<GoogleTagManager />

			<Flex width={[1, 1, 8 / 12]} justifyContent="center" py={[4, 4, 5]} px={4}>
				<Box maxWidth={720} flexGrow={1}>
					<Jumbo />

					<Box maxWidth={500}>
						<FormikProvider value={formik}>
							<Form>
								<Box mb={3} mt={4}>
									<Label mb={2}>
										{t('onboarding.form.companyName')}
										<small>*</small>
									</Label>
									<Input.Formik name="companyName" onFocus={() => setFocus('companyName')} />
								</Box>

								<Separator my={4} />

								<Box mb={3}>
									<Label mb={2}>
										{t('onboarding.form.userName')}
										<small>*</small>
									</Label>
									<Input.Formik name="userName" onFocus={() => setFocus('userName')} />
								</Box>

								<Box mb={3}>
									<Label mb={2}>
										{t('onboarding.form.email')}
										<small>*</small>
									</Label>
									<Input.Formik name="email" type="email" onFocus={() => setFocus('email')} />
								</Box>

								<Separator my={4} />

								{error && (
									<Box mb={3}>
										<Callout
											title={t('onboarding.form.error.title')}
											description={t('onboarding.form.error.description')}
											color="danger"
										/>
									</Box>
								)}

								<Switch
									error={!!formik.errors.acceptsPolicies && formik.touched.acceptsPolicies}
									on={formik.values.acceptsPolicies}
									onChange={(on) => formik.setValues({ ...formik.values, acceptsPolicies: on })}
									label={
										<Paragraph>
											<Trans
												t={t}
												i18nKey={'onboarding.form.policies'}
												components={{
													tc: (
														<TermsLink href="https://terms.ontreams.com/Algemene%20voorwaarden%20v1.0.pdf" />
													),
													pa: (
														<TermsLink href="https://terms.ontreams.com/Algemene%20voorwaarden%20v1.0.pdf" />
													),
													pp: (
														<TermsLink href="https://terms.ontreams.com/Treams+App+-+Privacy+statement+Treams+B.V.+2021+v1.5+(NL).pdf" />
													),
												}}
											/>
										</Paragraph>
									}
								/>
								<Button
									loading={loading}
									title={t('onboarding.form.submit')}
									onClick={async () => {
										mixpanel.track('signup.submit');
										await formik.submitForm();
									}}
									mt={4}
								/>
							</Form>
						</FormikProvider>
						<LoginAttemptCard />
					</Box>
				</Box>
			</Flex>

			<Sidebar />
		</Layout>
	);
}
